// O Fio rodando DENTRO do app do Centrão.
//
// O app não é uma segunda implementação do Fio: ele exibe este mesmo site numa
// WebView. Quase tudo funciona igual — inclusive login, SSE e mídia. Mas três
// coisas do navegador não existem lá dentro, e as três somem CALADAS (sem erro,
// sem console, nada acontece quando a pessoa clica):
//
//   1. `<a download>` apontando pra `blob:` — é assim que o Fio entrega o
//      extrato, o CSV dos indicadores, o .vcf da agenda e o documento da
//      conversa;
//   2. `window.open` — a casca do app resolve isso sozinha, sem precisar deste
//      arquivo (ela troca a função por uma que funciona);
//   3. Web Push — não existe `PushManager` numa WebView. Ver `src/push/push.js`.
//
// Este módulo é o único lugar que sabe da diferença. Todo o resto do Fio
// continua escrito pro navegador.

/** A ponte que a casca injeta antes da página carregar, ou null no navegador. */
export function ponte() {
  return (typeof window !== 'undefined' && window.FioApp) || null;
}

/** true quando o Fio está rodando dentro do app instalado. */
export function noApp() {
  return !!ponte();
}

/** Versão do app instalado (só faz sentido dentro dele). */
export function versaoDoApp() {
  return ponte()?.versao || null;
}

// No navegador: o truque de sempre — âncora invisível com `download`.
function baixarNoNavegador(nomeArquivo, blob) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revogar na hora corta o download no Firefox e em WebView antiga; o atraso
  // custa alguns KB de memória por alguns segundos e evita arquivo truncado.
  setTimeout(() => window.URL.revokeObjectURL(url), 4000);
}

/**
 * Entrega um arquivo que o próprio Fio montou aqui (extrato, CSV, .vcf).
 *
 * No app o arquivo vai pela ponte e o sistema abre a folha de compartilhamento,
 * onde a pessoa escolhe: abrir, salvar em Arquivos ou mandar adiante. Um passo
 * a mais que no computador, e nenhum arquivo perdido numa pasta que ela não
 * acha.
 *
 * Só pra conteúdo pequeno gerado no cliente. Mídia de conversa (vídeo de
 * dezenas de MB) usa `baixarDaUrl`.
 */
export async function baixarArquivo(nomeArquivo, blob) {
  const app = ponte();
  if (!app) {
    baixarNoNavegador(nomeArquivo, blob);
    return;
  }
  await app.baixarBlob(nomeArquivo, blob);
}

/**
 * Entrega um arquivo que mora numa URL assinada (mídia da conversa).
 *
 * No app quem baixa é o lado nativo, direto da URL — passar um vídeo por dentro
 * da ponte, virado em texto, derruba o aplicativo.
 */
export async function baixarDaUrl(nomeArquivo, url) {
  const app = ponte();
  if (!app) {
    const r = await fetch(url);
    if (!r.ok) throw new Error('falhou');
    baixarNoNavegador(nomeArquivo, await r.blob());
    return;
  }
  await app.baixarUrl(nomeArquivo, url);
}

/** Abre um link fora do Fio (mapa, boleto, site de terceiro). */
export function abrirExterno(url) {
  const app = ponte();
  if (app) app.abrirExterno(url);
  else window.open(url, '_blank', 'noopener');
}

/**
 * Bolinha com o número no ícone do app.
 *
 * Veio da versão nativa, onde a própria tela contava. Agora quem informa é o
 * Fio — a barra lateral já busca `/atendimentos/nao-lidas/total` pra sua
 * própria badge, então é o mesmo número, sem consulta nova.
 *
 * No navegador não faz nada: ícone de aba não tem contador.
 */
export function avisarBadge(total) {
  ponte()?.badge(total);
}

/**
 * Diz ao app qual tema o Fio está usando (claro ou escuro).
 *
 * A moldura do app — área de status, fundo e a tela de sem-conexão — é pintada
 * pelo lado nativo, que não enxerga o CSS daqui. Sem este aviso, Fio claro
 * aparece dentro de moldura escura e a faixa do topo entrega que são duas
 * coisas coladas.
 */
export function avisarTema(nome) {
  ponte()?.tema(nome);
}

